import React, { lazy, Suspense } from 'react';
import { useCallMode } from '../../context/CallModeContext';
import DialerWidget from './DialerWidget';
import DialerSkeleton from './DialerSkeleton';

const TeleCMIDialer = lazy(() => import('./TeleCMIDialer'));

const DialerProvider = ({ phoneNumber = '', embedded = false, isHost = false }) => {
  const { providerType, providerLoading } = useCallMode();

  // Never guess the provider. Until the org's provider_type resolves we cannot know
  // which dialer to mount, and falling through to DialerWidget was rendering the legacy
  // JsSIP dialer for TeleCMI orgs — visible as a stuck "Offline / N/A" status bar that
  // only a page refresh cleared. For the headless host this also mattered: mounting the
  // wrong host registers a JsSIP UA and publishes the wrong dialerActionsRef, so embedded
  // call buttons delegated into the wrong implementation.
  if (providerLoading || providerType === null) {
    return isHost ? null : <DialerSkeleton />;
  }
  if (providerType === 'telecmi') {
    return (
      <Suspense fallback={<DialerSkeleton />}>
        <TeleCMIDialer phoneNumber={phoneNumber} embedded={embedded} isHost={isHost} />
      </Suspense>
    );
  }
  if (providerType === 'phonecall') {
    // Mobile SIM: the agent places the call on their own phone via the CRM Agent
    // app. There is no browser softphone to mount — falling through to DialerWidget
    // (JsSIP) would register a SIP UA and stick on "Offline / N/A" like the legacy
    // TeleCMI bug above. The headless host renders nothing.
    if (isHost) return null;
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Mobile SIM calling</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Place and manage calls from the CRM Agent app on your phone — this
          organisation&apos;s calls are carried on the agent&apos;s mobile SIM.
        </p>
      </div>
    );
  }
  return <DialerWidget phoneNumber={phoneNumber} embedded={embedded} isHost={isHost} />;
};

export default DialerProvider;
